import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { AssetsQueryInvalidator } from '../../projectView/queries/queryInvalidator';
import ProjectMember, { ProjectMemberJSON } from '../models/projectMember';
import useProjectMembersSystem from '../system/useProjectMembersSystem';
import { QueryKeys } from './keysAndFilters';

export type UseDeleteProjectMemberMutationParams = {
	projectID: number;
	onSuccess?: ( member: ProjectMember ) => void,
	onError?: ( error: unknown ) => void
};

const useDeleteProjectMemberMutation = ( {
	projectID, onSuccess: onMutationSuccess, onError: onMutationError
}: UseDeleteProjectMemberMutationParams ) => {
	const system = useProjectMembersSystem();
	const queryClient = useQueryClient();

	const onSuccess = useCallback( ( _data: ProjectMemberJSON, member: ProjectMember ) => {
		queryClient.invalidateQueries( {
			queryKey: QueryKeys.forProjectMembers( { projectID } ),
			refetchType: 'active'
		} );

		AssetsQueryInvalidator.invalidateProjectOverviews( projectID, { queryClient } );

		onMutationSuccess?.( member );
	}, [ onMutationSuccess, projectID, queryClient ] );

	return useMutation(
		( member: ProjectMember ) => system.deleteProjectMember( { projectID, memberID: member.id } ),
		{ onSuccess, onError: onMutationError }
	);
}

export default useDeleteProjectMemberMutation;
